import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useSession } from '@/features/auth';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton, useToastStore } from '@/shared/ui';

import { buildTiles, greetingPart, previewOf } from '../model/dashboard';
import type { Tile, TileItem, TileMeta } from '../model/dashboard';
import { waitingAge } from '../model/ordering';
import type { CommitmentSummary } from '../model/types';
import { useAcknowledgeNotice, useDashboard } from '../queries/useCommitments';
import styles from './DashboardScreen.module.css';
import { TILE_ICONS } from './tileIcons';

/**
 * Pulpit — wariant **R1 „Kafle"** (decyzja 60).
 *
 * ⭐ Rola ekranu wynika z decyzji 33: pulpit jest **widokiem informacyjnym**, którego
 * głównym zadaniem jest **przeniesienie do właściwego miejsca**. Nie jest tablicą pracy
 * ani kolejką decyzji — te warianty odpadły, bo akcji jest tu mało. Stąd jedna jedyna
 * akcja na całym ekranie (decyzja 66, B3) i sześć kafli, które przede wszystkim linkują.
 *
 * Kolejność kafli jest **stała, zgodna z menu** — pilność niesie licznik i kolor.
 * Makieta R1 układa je „najpilniejsze najpierw" i decyzja 66 świadomie to odrzuca:
 * kafle skaczące między wizytami niszczą odruch, który jest jedyną przewagą tego wariantu.
 */

export function DashboardScreen() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const session = useSession();
  const dashboard = useDashboard();

  if (dashboard.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton height="11rem" announce />
        <Skeleton height="11rem" />
        <Skeleton height="11rem" />
        <Skeleton height="11rem" />
      </div>
    );
  }

  if (dashboard.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(dashboard.error)}</p>
        <Button onClick={() => void dashboard.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  const tiles = buildTiles(dashboard.data);

  return (
    <div className={styles['screen']}>
      <header className={styles['head']}>
        {/* Powitanie zna porę dnia — „Good morning" o 19:00 podważa liczby stojące niżej. */}
        <h1 className={styles['greeting']}>
          {t(`dashboard.greeting.${greetingPart(new Date())}`, {
            name: firstName(session.data?.displayName),
          })}
        </h1>
        <p className={styles['subtitle']}>{t('dashboard.subtitle')}</p>
      </header>

      <div className={styles['grid']}>
        {tiles.map((tile) => (
          <TileCard key={tile.id} tile={tile} />
        ))}
      </div>
    </div>
  );
}

function TileCard({ tile }: { tile: Tile }) {
  const { t } = useTranslation();
  const items = previewOf(tile);
  const TileIcon = TILE_ICONS[tile.id];

  return (
    <section
      className={`${styles['tile']} ${tile.urgent ? styles['tileUrgent'] : ''}`}
      aria-labelledby={`tile-${tile.id}`}
    >
      <header className={styles['tileHead']}>
        {/* Ikona listy przychodzi z `TILE_ICONS`, wspólnego z menu powłoki — kafel
            i pozycja menu opisują tę samą listę, więc nie mogą wyglądać inaczej. */}
        <TileIcon className={styles['icon']} size={17} aria-hidden="true" />

        <h2 id={`tile-${tile.id}`} className={styles['tileTitle']}>
          {t(`dashboard.tile.${tile.id}`)}
        </h2>

        <span className={`${styles['count']} ${tile.urgent ? styles['countUrgent'] : ''}`}>
          {tile.count}
        </span>
      </header>

      {items.length > 0 ? (
        <ul className={styles['items']}>
          {items.map((item) => (
            <ItemRow key={item.key} item={item} />
          ))}
        </ul>
      ) : (
        /*
         * Pusty kafel to najsłabsze miejsce R1 („przy pustych listach ekran wygląda
         * na zepsuty" — wprost z makiety). Zdanie zamiast pustki jest tanią odpowiedzią:
         * mówi, że lista jest pusta, a nie że coś się nie wczytało.
         */
        <p className={styles['empty']}>{t('dashboard.empty')}</p>
      )}

      <footer className={styles['tileFoot']}>
        <span className={styles['rule']}>{t(`dashboard.rule.${tile.id}`)}</span>
      </footer>
    </section>
  );
}

function ItemRow({ item }: { item: TileItem }) {
  return (
    <li className={styles['item']}>
      {/*
        Adres z otwartym panelem zobowiązań (E1) — pulpit ma przenosić tam, gdzie da się
        z zadaniem coś zrobić, a nie tylko je obejrzeć.
      */}
      <Link
        className={styles['itemLink']}
        to={`/constellation/${item.hostElementId}?panel=commitments&el=${item.hostElementId}`}
        title={item.title}
      >
        {item.title}
      </Link>

      {item.noticeId === undefined ? (
        <span className={styles['itemMeta']}>
          <MetaText meta={item.meta} />
        </span>
      ) : (
        <span className={styles['itemAction']}>
          <AcknowledgeButton noticeId={item.noticeId} />
        </span>
      )}
    </li>
  );
}

/**
 * Druga linia wiersza. Warstwa modelu zwraca **klucz i wartości**, nie gotowe zdanie
 * (spec 4C), więc tłumaczenie dzieje się dopiero tutaj.
 */
function MetaText({ meta }: { meta: TileMeta }) {
  const { t } = useTranslation();

  // „wisi od" jest jedyną wartością liczoną wobec teraz, więc nie da się jej
  // przygotować w modelu — data z serwera zamienia się w wiek dopiero przy renderze.
  if (meta.key === 'commitments.tile.meta.since') {
    const at = String(meta.values?.['at'] ?? '');
    const age = waitingAge({ createdAtUtc: at } as CommitmentSummary, new Date());

    return <>{t(`commitments.age.${age.unit}`, { count: age.value })}</>;
  }

  return <>{t(meta.key.replace('commitments.tile.', 'dashboard.tile.'), meta.values ?? {})}</>;
}

/**
 * Jedyna akcja pulpitu (decyzja 66, B3) — i wyłącznie na kaflu „Do wiadomości".
 *
 * To jedyna lista, która **bez akcji nigdy się nie wyzeruje**: wszystko inne zamyka się
 * tam, gdzie mieszka robota. Więcej akcji wymagałoby kontekstu, którego pulpit nie ma.
 */
function AcknowledgeButton({ noticeId }: { noticeId: number }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const acknowledge = useAcknowledgeNotice();

  return (
    <Button
      variant="ghost"
      disabled={acknowledge.isPending}
      /*
       * Etykieta krótka, pełne zdanie w nazwie dostępnej. „Przyjąłem do wiadomości"
       * w kaflu zjadało tytuł zadania, czyli tę część wiersza, po której człowiek
       * w ogóle rozpoznaje, o czym jest wiadomość.
       */
      aria-label={t('dashboard.acknowledgeFull')}
      onClick={() =>
        acknowledge.mutate(noticeId, {
          onSuccess: () => show({ message: t('dashboard.acknowledged'), tone: 'success' }),
          onError: (error) => show({ message: describeError(error), tone: 'danger' }),
        })
      }
    >
      {t('dashboard.acknowledge')}
    </Button>
  );
}

/**
 * Powitanie po imieniu. ⚠ Dzielimy po spacji, a nie zakładamy „imię nazwisko":
 * konto może mieć jednoczłonową nazwę wyświetlaną i wtedy pusty człon dałby „Dzień dobry, ".
 */
function firstName(displayName: string | undefined): string {
  return displayName?.trim().split(/\s+/)[0] ?? '';
}
